// Карточка задачи на доске.
import { useState } from 'react'
import { STATUS_TONES, formatDateTime, timeAgo } from '../lib/format.ts'
import { isDoneUnseen } from '../lib/outcome.ts'
import { TaskLink, openTask } from '../router.tsx'
import { useAgentMap, useLive, useNow } from '../store.ts'
import { TASK_STATUS_LABELS, agentTime, type TaskCard, type WaitingOn } from '../types.ts'
import { AgentAvatar, AgentLabel } from './AgentAvatar.tsx'
import { DirectionChip, PriorityIcon, TaskKindBadge } from './chips.tsx'
import { Icon } from './Icon.tsx'
import { Badge, Dot, LiveDot, QueuedDot, cx } from './ui.tsx'

/** Описание ожидания служебными словами («subtasks, timer») — так писали до 20.10; сервер пересобирает их сам. */
const LEGACY_WAIT_NOTE = /^(question|escalation|subtasks|timer)(, (question|escalation|subtasks|timer))*$/

/** Сроки — во времени агентов (часы в шапке), а не в поясе браузера: «до 14:45 МСК». */
export function waitingText(w: WaitingOn | null, agentName: (id: string) => string, tz?: string): string | null {
  if (!w) return null
  if (w.type === 'review') return 'на проверке'
  const note = w.note && !LEGACY_WAIT_NOTE.test(w.note) ? w.note : ''
  const until = w.until ? `до ${agentTime(w.until, tz)}` : ''
  if (w.type === 'timer') return `ждёт ${until || 'времени'}${note ? ` — ${note}` : ''}`
  if (note) return `ждёт: ${note}${until ? ` · вернётся к задаче ${until}` : ''}`
  switch (w.type) {
    case 'human':
      return 'ждёт твоего ответа'
    case 'agent':
      return w.ids.length && w.ids.every((x) => typeof x === 'string') ? `ждёт: ${w.ids.map(String).map(agentName).join(', ')}` : 'ждёт агентов'
    case 'subtasks':
      return 'ждёт подзадачи'
    default:
      return 'ждёт'
  }
}

export function SubtaskProgress({ done, total }: { done: number; total: number }) {
  if (!total) return null
  const pct = Math.round((done / total) * 100)
  return (
    <span className="inline-flex items-center gap-1.5" title={`Подзадачи: ${done} из ${total}`}>
      <span className="relative h-1 w-8 overflow-hidden rounded-full bg-surface-3" aria-hidden="true">
        <span
          className={cx('absolute inset-y-0 left-0 rounded-full', done === total ? 'bg-ok' : 'bg-subtle')}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="tnum">
        {done}/{total}
      </span>
    </span>
  )
}

export function TaskCardView({
  card,
  depth = 0,
  parentLabel,
  nested,
  dragging,
  onDragStart,
  onDragEnd,
}: {
  card: TaskCard
  depth?: number
  /** Подпись родителя, если подзадача показана отдельной карточкой. */
  parentLabel?: string | null
  /** Вложенные подзадачи для раскрытия прямо в карточке. */
  nested?: TaskCard[]
  dragging?: boolean
  onDragStart?: (id: number) => void
  onDragEnd?: () => void
}) {
  const now = useNow()
  const agents = useAgentMap()
  const tz = useLive((s) => s.settings?.agent_timezone)
  const [open, setOpen] = useState(false)
  const cross = card.kind === 'cross' || card.direction_ids.length > 1
  const waiting = card.status === 'waiting_agents' || card.status === 'waiting_human' ? waitingText(card.waiting_on, (id) => agents.get(id)?.name ?? id, tz) : null
  const isDone = card.status === 'done' || card.status === 'cancelled'

  return (
    <div
      draggable={Boolean(onDragStart)}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(card.id))
        e.dataTransfer.effectAllowed = 'move'
        onDragStart?.(card.id)
      }}
      onDragEnd={() => onDragEnd?.()}
      onClick={() => openTask(card.id)}
      className={cx(
        'group relative cursor-pointer rounded-card border border-line bg-surface px-3 py-2.5 shadow-card transition-[border-color,box-shadow,opacity]',
        'hover:border-line-strong hover:shadow-pop',

        dragging && 'opacity-40',
        isDone && !isDoneUnseen(card) && 'opacity-75',
      )}
      style={depth ? { marginLeft: Math.min(depth, 3) * 14 } : undefined}
    >
      {card.needs_attention || card.open_escalations > 0 ? (
        <span aria-hidden="true" className="absolute inset-y-2.5 left-[-1px] w-[3px] rounded-r bg-warn" />
      ) : null}
      <div className="flex min-w-0 items-center gap-1.5 overflow-hidden text-[11.5px] text-muted">
        <PriorityIcon priority={card.priority} />
        <span className="tnum shrink-0 font-mono text-[11px] whitespace-nowrap text-subtle">{card.key}</span>
        {card.running ? (
          <LiveDot label="Агент работает над задачей" className="ml-0.5" />
        ) : card.queued ? (
          <QueuedDot label="В очереди — причина ожидания в карточке задачи" className="ml-0.5" />
        ) : null}
        <TaskKindBadge kind={card.kind} cross={cross} />
        {isDoneUnseen(card) ? (
          <Badge tone="ok" dot title="Агенты закончили — вы ещё не смотрели итог">
            Новый итог
          </Badge>
        ) : null}
        {card.open_escalations > 0 ? (
          <Badge tone="warn" title="Открытые вопросы к тебе">
            <Icon name="flag" size={11} />
            {card.open_escalations}
          </Badge>
        ) : null}
      </div>

      {parentLabel ? (
        <div className="mt-1 flex min-w-0 items-center gap-1 text-[11.5px] text-subtle" title={parentLabel}>
          <Icon name="cornerDownRight" size={12} className="shrink-0" />
          <span className="truncate">{parentLabel}</span>
        </div>
      ) : null}

      <TaskLink
        id={card.id}
        className={cx('mt-1 line-clamp-3 block text-[13.5px] leading-snug font-medium text-fg [overflow-wrap:anywhere] hover:underline', isDone && 'text-muted')}
        draggable={false}
      >
        {card.title}
      </TaskLink>

      {card.direction_ids.length ? (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {card.direction_ids.slice(0, 4).map((d) => (
            <DirectionChip key={d} id={d} />
          ))}
          {card.direction_ids.length > 4 ? (
            <span className="text-[11px] text-subtle">+{card.direction_ids.length - 4}</span>
          ) : null}
        </div>
      ) : null}

      {waiting ? (
        <div
          className={cx(
            'mt-1.5 line-clamp-2 text-[12px] [overflow-wrap:anywhere]',
            card.status === 'waiting_human' ? 'font-medium text-warn' : 'text-muted',
          )}
        >
          {waiting}
        </div>
      ) : null}

      <div className="mt-2.5 flex min-w-0 items-center gap-2 text-[12px] text-muted">
        {card.assignee_agent_id ? (
          <AgentLabel id={card.assignee_agent_id} size="xs" className="min-w-0 max-w-[60%]" />
        ) : (
          <span className="text-subtle italic">{card.status === 'inbox' || card.status === 'triage' ? 'Штаб разберёт' : 'не назначена'}</span>
        )}
        {card.participants.filter((p) => p !== card.assignee_agent_id).length ? (
          <span className="flex shrink-0 -space-x-1" title="Участвовали агенты">
            {card.participants
              .filter((p) => p !== card.assignee_agent_id)
              .slice(0, 3)
              .map((p) => (
                <AgentAvatar key={p} agent={agents.get(p)} size="xs" fallbackName={p} className="ring-1 ring-surface" />
              ))}
          </span>
        ) : null}
        <span className="ml-auto flex shrink-0 items-center gap-2.5">
          <span className="tnum text-[11.5px] whitespace-nowrap text-subtle" title={`Последняя активность: ${formatDateTime(card.last_activity_at)}`}>
            {timeAgo(card.last_activity_at, now)}
          </span>
          {card.messages_count > 0 ? (
            <span className="tnum inline-flex items-center gap-0.5" title={`Сообщений: ${card.messages_count}`}>
              <Icon name="message" size={12} />
              {card.messages_count}
            </span>
          ) : null}
          {card.subtasks_total > 0 ? (
            nested && nested.length ? (
              <button
                type="button"
                className="inline-flex items-center gap-0.5 rounded px-0.5 hover:bg-surface-2 hover:text-fg"
                aria-expanded={open}
                aria-label={open ? 'Скрыть подзадачи' : 'Показать подзадачи'}
                onClick={(e) => {
                  e.stopPropagation()
                  setOpen((v) => !v)
                }}
              >
                <Icon name="chevronRight" size={12} className={cx('transition-transform', open && 'rotate-90')} />
                <SubtaskProgress done={card.subtasks_done} total={card.subtasks_total} />
              </button>
            ) : (
              <SubtaskProgress done={card.subtasks_done} total={card.subtasks_total} />
            )
          ) : null}
        </span>
      </div>

      {open && nested?.length ? (
        <ul className="mt-2 space-y-0.5 border-t border-line pt-1.5" onClick={(e) => e.stopPropagation()}>
          {nested.map((s) => (
            <li key={s.id}>
              <TaskLink
                id={s.id}
                className="flex min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-[12px] hover:bg-surface-2"
                title={`${s.key} · ${TASK_STATUS_LABELS[s.status]}`}
              >
                <Dot tone={STATUS_TONES[s.status]} />
                <span className="tnum shrink-0 font-mono text-[10.5px] text-subtle">{s.key}</span>
                <span className={cx('min-w-0 flex-1 truncate', s.status === 'done' && 'text-muted line-through')}>
                  {s.title}
                </span>
                {s.running ? <LiveDot /> : s.queued ? <QueuedDot /> : null}
                {s.assignee_agent_id ? (
                  <AgentAvatar agent={agents.get(s.assignee_agent_id)} size="xs" fallbackName={s.assignee_agent_id} />
                ) : null}
              </TaskLink>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
